import { DoubleSide, Mesh, PlaneBufferGeometry, ShaderMaterial, Vector4 } from 'three';
import type { IUniform } from 'three';
import { Vector2 } from 'three';

import type { Palette, PalettedColor } from 'shared/interfaces/planogram';
import { SphereItemType } from 'shared/interfaces/planogram';
import type { BaseObjectUserData } from 'shared/object3d/interfaces';
import type { ShapeObjectConfig } from 'shared/interfaces/object_config';
import { BorderSide } from 'shared/interfaces/planogram';
import standardVertexShader from 'shared/renderingEngine/shaders/StandardVertexShader.glsl';
import shapeFragmentShader from 'shared/renderingEngine/shaders/ShapeFragmentShader.glsl';

import BaseObject3D from 'shared/object3d/BaseObject3D';
import { hexToRgbaArray, resolvePaletteColor, resolvePaletteVector4 } from 'shared/utils/colors';

const UNSET_COLOR = new Vector4(1, 0, 0, 1);

interface ShapeMaterialUniforms {
  opacity: IUniform<number>;
  fillColor: IUniform<Vector4>;
  borderColor: IUniform<Vector4>;
  borderSize: IUniform<number>;
  scale: IUniform<Vector2>;
  cornerRadius: IUniform<Vector4>;
  [key: string]: IUniform<unknown>; // force it to be accepted by new ShaderMaterial
}

// TODO: get rid of meshes in user data?
interface ShapeUserData extends BaseObjectUserData {
  mesh: Mesh<PlaneBufferGeometry, ShaderMaterial>;
}

function isBorderVisible(shapeConfig: ShapeObjectConfig) {
  return shapeConfig.borderOpacity > 0 && shapeConfig.thickness > 0;
}

function isFillVisible(shapeConfig: ShapeObjectConfig) {
  return shapeConfig.fillOpacity > 0 && shapeConfig.scale[0] > 0 && shapeConfig.scale[1] > 0;
}

function isShapeVisible(shapeConfig: ShapeObjectConfig) {
  return isBorderVisible(shapeConfig) || isFillVisible(shapeConfig);
}

function hasCornerRadius(shapeConfig: ShapeObjectConfig) {
  return shapeConfig.cornerRadius.some(it => it > 0);
}

const geometry = new PlaneBufferGeometry(1, 1, 1);

class ShapeObject3D extends BaseObject3D<ShapeUserData> {
  constructor() {
    super();
    this.itemType = SphereItemType.Shape;
  }

  private renderScale = new Vector2();
  getRenderScale(output: Vector2) {
    return output.copy(this.renderScale);
  }

  private boundsScale = new Vector2();
  getBoundsScale(output: Vector2) {
    return output.copy(this.boundsScale);
  }

  updateScale(scale: Vector2, shapeConfig: ShapeObjectConfig) {
    this.boundsScale = scale.clone();
    this.renderScale = scale.clone();
    switch (shapeConfig.borderSide) {
      case BorderSide.Inside:
        break;
      case BorderSide.Centered:
        this.renderScale.addScalar(2 * shapeConfig.thickness);
        break;
      case BorderSide.Outside:
        this.renderScale.addScalar(4 * shapeConfig.thickness);
        break;
    }
    this.userData.mesh.material.uniforms.scale.value = this.renderScale;
    const mesh = this.userData.mesh;
    const relativeScale = this.renderScale.clone().divide(scale);
    mesh.scale.set(relativeScale.x, relativeScale.y, 1);
  }

  configure(shapeConfig: ShapeObjectConfig) {
    const defines: Record<string, string> = {};
    if (isBorderVisible(shapeConfig)) defines.BORDER = '';
    const uniforms = {
      opacity: { value: 1 },
      borderSize: { value: shapeConfig.thickness * 2 }, // the previous implementation rendered the border at twice the size listed in the UI
      borderColor: { value: UNSET_COLOR },
      fillColor: { value: UNSET_COLOR },
      scale: { value: this.renderScale },
      cornerRadius: { value: new Vector4(...shapeConfig.cornerRadius) },
    };
    const material = new ShaderMaterial({
      vertexShader: standardVertexShader,
      fragmentShader: shapeFragmentShader,
      depthTest: false,
      uniforms,
      defines,
      transparent: true,
      side: DoubleSide,
    });

    const mesh = new Mesh(geometry, material);
    mesh.name = 'ShapeMesh';

    this.userData.mesh = mesh;
    this.add(mesh);

    const scale = new Vector2(shapeConfig.scale[0], shapeConfig.scale[1]);
    this.updateScale(scale, shapeConfig);
  }

  // TODO: merge with configure
  rebuild(shapeConfig: ShapeObjectConfig, palette: Palette | undefined) {
    const scale = new Vector2(shapeConfig.scale[0], shapeConfig.scale[1]);
    this.updateScale(scale, shapeConfig);

    this.visibilityOptimisation(isShapeVisible(shapeConfig));
    const material = this.userData.mesh.material;
    const enableBorder = isBorderVisible(shapeConfig);
    const oldEnableBorder = material.defines.BORDER !== undefined;
    if (enableBorder !== oldEnableBorder) {
      material.defines.BORDER = enableBorder ? '' : undefined;
      material.needsUpdate = true;
    }
    const enableCornerRadius = hasCornerRadius(shapeConfig);
    const oldEnableCornerRadius = material.defines.CORNER_RADIUS !== undefined;
    if (enableCornerRadius !== oldEnableCornerRadius) {
      material.defines.CORNER_RADIUS = enableCornerRadius ? '' : undefined;
      material.needsUpdate = true;
    }
    this.setMaterialUniforms(this.configToUniforms(shapeConfig, palette));
  }

  private visibilityOptimisation(visible: boolean) {
    if (this.itemType !== SphereItemType.Shape) return;
    this.visible = visible;
    this.userData.mesh.visible = visible;
  }

  overrideOpacity(opacity: number): void {
    this.visibilityOptimisation(opacity > 0);
    const material = this.userData.mesh.material;
    const shading = material.uniforms.shading;
    if (shading?.value !== undefined && material.userData.tempOpacityApplied !== true) {
      material.userData.originalOpacity = shading.value.w;
      shading.value = shading.value.clone().setW(opacity);
      material.userData.tempOpacityApplied = true;
    }
  }

  resetOpacity(): void {
    const material = this.userData.mesh.material;
    this.visibilityOptimisation(material.userData.originalOpacity > 0);
    const shading = material.uniforms.shading;
    if (shading?.value !== undefined && material.userData.tempOpacityApplied === true) {
      shading.value = shading.value.clone().setW(material.userData.originalOpacity);
      material.userData.tempOpacityApplied = false;
    }
  }

  private setMaterialUniforms(uniforms: ShapeMaterialUniforms) {
    const material = this.userData.mesh.material;

    Object.entries(uniforms).forEach(([name, uniform]) => {
      material.uniforms[name].value = uniform.value;
    });
  }

  private configToUniforms(shapeConfig: ShapeObjectConfig, palette: Palette | undefined) {
    const uniforms: ShapeMaterialUniforms = {
      opacity: { value: 1 },
      borderSize: { value: shapeConfig.thickness * 2 }, // the previous implementation rendered the border at twice the size listed in the UI
      borderColor: {
        value: resolvePaletteVector4(shapeConfig.borderColor, shapeConfig.borderOpacity, palette),
      },
      fillColor: {
        value: resolvePaletteVector4(shapeConfig.fillColor, shapeConfig.fillOpacity, palette),
      },
      scale: { value: this.renderScale },
      cornerRadius: { value: new Vector4(...shapeConfig.cornerRadius) },
    };
    return uniforms;
  }

  dispose() {
    const mesh = this.userData.mesh;
    mesh?.material?.dispose();
    mesh?.geometry?.dispose();
  }
}

export default ShapeObject3D;
